import isValid from 'date-fns/isValid';
import { snakeCase } from 'lodash';
import { startOfToday, endOfToday, endOfYesterday, startOfYesterday, startOfMonth, endOfMonth, endOfYear, startOfYear, subMonths, startOfDay, endOfDay, startOfTomorrow, endOfTomorrow } from 'date-fns';
export var Operators;
(function (Operators) {
    Operators["is"] = "is";
    Operators["isNot"] = "isNot";
    Operators["contains"] = "contains";
    Operators["doesNotContain"] = "doesNotContain";
    Operators["in"] = "in";
    Operators["notIn"] = "notIn";
    Operators["greaterThan"] = "greaterThan";
    Operators["lessThan"] = "lessThan";
    Operators["today"] = "today";
    Operators["afterToday"] = "afterToday";
    Operators["beforeToday"] = "beforeToday";
    Operators["yesterday"] = "yesterday";
    Operators["tomorrow"] = "tomorrow";
    Operators["thisMonth"] = "thisMonth";
    Operators["lastMonth"] = "lastMonth";
    Operators["last12Months"] = "last12Months";
    Operators["thisYear"] = "thisYear";
    Operators["range"] = "range";
    Operators["notSet"] = "notSet";
})(Operators || (Operators = {}));
export var BackendOperators;
(function (BackendOperators) {
    BackendOperators["is"] = "eq";
    BackendOperators["isNot"] = "not_eq";
    BackendOperators["contains"] = "match";
    BackendOperators["doesNotContain"] = "not_match";
    BackendOperators["in"] = "in";
    BackendOperators["notIn"] = "not_in";
    BackendOperators["greaterThan"] = "gt";
    BackendOperators["lessThan"] = "lt";
    BackendOperators["range"] = "range";
    BackendOperators["notSet"] = "is_null";
})(BackendOperators || (BackendOperators = {}));
export var FilterTypes;
(function (FilterTypes) {
    FilterTypes["date"] = "date";
    FilterTypes["text"] = "text";
    FilterTypes["boolean"] = "boolean";
    FilterTypes["number"] = "number";
    FilterTypes["select"] = "select";
    FilterTypes["async"] = "async";
    FilterTypes["radio"] = "radio";
})(FilterTypes || (FilterTypes = {}));
export const isNumberRange = (value) => Boolean(value) &&
    typeof value.from === 'number' &&
    typeof value.to === 'number' &&
    value.from !== undefined &&
    value.to !== undefined;
export const isDateRange = (value) => !!value &&
    isValid(new Date(value.from)) &&
    isValid(new Date(value.to));
export const isDateNotSet = (value) => !value;
export const isDateSingle = (value) => !!value && isValid(new Date(value.value));
export const dateMapper = (operator, rangeDates) => {
    let dateFrom = startOfToday();
    let dateTo = endOfToday();
    let dateValue = endOfToday();
    switch (operator) {
        case Operators.yesterday:
            dateFrom = startOfYesterday();
            dateTo = endOfYesterday();
            break;
        case Operators.tomorrow:
            dateFrom = startOfTomorrow();
            dateTo = endOfTomorrow();
            break;
        case Operators.thisMonth:
            dateFrom = startOfMonth(dateFrom);
            dateTo = endOfMonth(dateFrom);
            break;
        case Operators.thisYear:
            dateFrom = startOfYear(dateFrom);
            dateTo = endOfYear(dateFrom);
            break;
        case Operators.lastMonth:
            dateFrom = startOfMonth(subMonths(dateFrom, 1));
            dateTo = endOfMonth(subMonths(dateTo, 1));
            break;
        case Operators.last12Months:
            dateFrom = startOfDay(subMonths(dateFrom, 12));
            dateTo = endOfDay(dateTo);
            break;
        case Operators.range:
            if (rangeDates && rangeDates.from && rangeDates.to) {
                dateFrom = startOfDay(new Date(rangeDates.from));
                dateTo = endOfDay(new Date(rangeDates.to));
            }
            break;
        case Operators.afterToday:
            dateValue = endOfToday();
            break;
        case Operators.beforeToday:
            dateValue = startOfToday();
            break;
    }
    if (operator === Operators.afterToday || operator === Operators.beforeToday) {
        return {
            condition: BackendOperators[operator === Operators.beforeToday
                ? Operators.lessThan
                : Operators.greaterThan],
            value: dateValue
        };
    }
    return operator === Operators.range &&
        !(rangeDates && rangeDates.from && rangeDates.to)
        ? undefined
        : {
            condition: BackendOperators[Operators.range],
            from: dateFrom,
            to: dateTo
        };
};
export class FiltersService {
    constructor() {
        this.mapFiltersForQuery = (filters) => {
            const mappedFilters = Object.keys(filters).reduce((previousFilters, filterKey) => {
                const filter = filters[filterKey];
                let filterMapped;
                if (filter.type === FilterTypes.boolean) {
                    filterMapped = { value: filter.value ? 1 : 0 };
                }
                else if (filter.type === FilterTypes.date) {
                    filterMapped = this.mapDateFilterParams(filter);
                }
                else if (filter.type === FilterTypes.number) {
                    filterMapped = this.mapNumberFilterParams(filter);
                }
                else {
                    const newFilter = filter;
                    filterMapped = {
                        condition: BackendOperators[newFilter.operator || 'is'],
                        value: filter.value
                    };
                }
                const filterId = snakeCase(filter.id || filterKey);
                return {
                    ...previousFilters,
                    [filterId]: filterMapped
                };
            }, {});
            return mappedFilters;
        };
        this.mapDateFilterParams = (params) => {
            var _a, _b, _c;
            if (params.operator === Operators.notSet) {
                return {
                    condition: BackendOperators.notSet
                };
            }
            if (!params.operator) {
                throw new Error('missing operator');
            }
            const dates = dateMapper(params.operator, params.value);
            if (dates && dates.condition === BackendOperators.range) {
                return {
                    condition: dates.condition,
                    from: (_a = dates === null || dates === void 0 ? void 0 : dates.from) === null || _a === void 0 ? void 0 : _a.toISOString(),
                    to: (_b = dates === null || dates === void 0 ? void 0 : dates.to) === null || _b === void 0 ? void 0 : _b.toISOString()
                };
            }
            else {
                return {
                    condition: dates === null || dates === void 0 ? void 0 : dates.condition,
                    value: (_c = dates === null || dates === void 0 ? void 0 : dates.value) === null || _c === void 0 ? void 0 : _c.toISOString()
                };
            }
        };
        this.mapNumberFilterParams = (params) => {
            const numberFilter = params;
            if (numberFilter.operator === Operators.range) {
                const rangeValue = numberFilter.value;
                return {
                    condition: BackendOperators[Operators.range],
                    from: rangeValue.from,
                    to: rangeValue.to
                };
            }
            return {
                condition: BackendOperators[numberFilter.operator || Operators.is],
                value: params.value
            };
        };
    }
}
//# sourceMappingURL=filters.service.js.map